import { Component, ComponentWillLoad, h, Prop, State, Watch } from '@stencil/core';
import { injectHistory, LocationSegments, RouterHistory } from '@stencil/router';
import { store } from 'src/utils/store';
import { RenderHomePage } from 'src/components/functional/render-home-page';
import { All_DESIGN_TYPES, ALL_MODULES, PATH } from 'src/components/constants';
import svgSprite from './assets/sprite.svg';
import { ModuleEnum } from 'src/generated/operations';
import { RenderFireAndSmokeFork } from 'src/components/functional/render-fire-and-smoke-fork';
import { watchMediaQuery } from 'src/utils/watchMediaQuery';

@Component({
  tag: 'app-root',
  styleUrl: 'app-root.scss',
  shadow: true,
})
export class AppRoot implements ComponentWillLoad {

  @Prop({ mutable: true }) history: RouterHistory;

  @Prop({ mutable: true }) public location: LocationSegments;

  @Prop() root = '/';

  @Prop() withHeader = false;

  @Prop() imagesProxyUri: string = '';

  @Prop() serverUri: string = '';

  @State()
  private isOnHomePage = true;

  @Watch('location')
  public async locationChanged(newValue: LocationSegments, oldValue: LocationSegments): Promise<void> {
    this.isOnHomePage = newValue.pathname === this.root;
    // track only "virtual" navigation inside the app
    if (oldValue && oldValue.pathname.length > 0) {
      store.state.gaPush({
        event: "virtual_page_view",
        page_path: newValue.pathname,
        page_location: `${window.location}`,
      });
    }
  }

  @State() isMobile = false;

  private unWatchMedia: () => void = () => null;

  render() {
    return (
      <div class={{ main: true, 'is-website': !this.withHeader}}>
        <apollo-provider client={store.get('apollo')}>
          <main>
            <app-error-messages />
            <app-update-message />
            {store.state.isRegionDetected && <app-modal-scanning-process />}
            {this.withHeader && store.state.isRegionDetected && <app-header class={{ 'header--is-home': this.isOnHomePage, 'header': true }} />}
            {store.state.isRegionDetected && <request-form />}
            {store.state.isRegionDetected && <feedback-form />}
            {store.state.isRegionDetected &&
              <div class="container">
                <stencil-router>
                  <stencil-route-switch scrollTopOffset={0}>
                    <stencil-route url={this.root} exact={true} routeRender={
                      () => (<RenderHomePage isMobile={this.isMobile} />)
                    }/>
                    <stencil-route url={`${this.root}p`} exact={true}
                                   component="app-search-by-label"
                    />
                    <stencil-route url={`${this.root}p/`} exact={true}
                                   component="app-search-by-label"
                    />
                    <stencil-route url={`${this.root}${ModuleEnum.FireAndSmoke}`} exact={true}
                                   routeRender={() => (<RenderFireAndSmokeFork/>)}
                    />
                    <stencil-route url={`${this.root}${PATH.FILTER}/${ModuleEnum.FireAndSmokeSolution}/:page`}
                                   component="app-size-your-damper"
                    />
                    <stencil-route url={`${this.root}${PATH.FILTER}/${ModuleEnum.FireAndSmokeSolution}/:page/`}
                                   component="app-size-your-damper"
                    />
                    <stencil-route componentProps={{currentModule: ModuleEnum.FireAndSmokeSolution, currentPath:PATH.FILTER}}
                                   url={`${this.root}${PATH.FILTER_VALUES}/${PATH.FILTER}/${ModuleEnum.FireAndSmokeSolution}`}
                                   component="app-values"/>
                    <stencil-route componentProps={{currentModule: ModuleEnum.FireAndSmokeSolution, currentPath:PATH.FILTER}}
                                   url={`${this.root}${PATH.FILTER_VALUES}/${PATH.FILTER}/${ModuleEnum.FireAndSmokeSolution}/`}
                                   component="app-values"/>
                    {[...ALL_MODULES, ModuleEnum.Vau, ModuleEnum.Accessory]
                      .map((module) => [
                        <stencil-route componentProps={{currentModule: module}}
                                       url={`${this.root}${PATH.DETAILS}/${module}/:id/:label`}
                                       exact={true}
                                       component="app-details"/>,
                        <stencil-route componentProps={{currentModule: module}}
                                       url={`${this.root}${PATH.DETAILS}/${module}/:id/:label/`}
                                       exact={true}
                                       component="app-details"/>,
                        <stencil-route componentProps={{currentModule: module}}
                                       url={`${this.root}${PATH.DETAILS}/${module}/:id/:label/:page`}
                                       exact={true}
                                       component="app-details"/>,
                        <stencil-route componentProps={{currentModule: module}}
                                       url={`${this.root}${PATH.DETAILS}/${module}/:id/:label/:page/`}
                                       exact={true}
                                       component="app-details"/>,
                        <stencil-route componentProps={{currentModule: module}}
                                       url={`${this.root}${PATH.DETAILS}/${module}/:id/:label/:page`}
                                       exact={true}
                                       component="app-details"/>,
                        <stencil-route componentProps={{currentModule: module}}
                                       url={`${this.root}${PATH.DETAILS}/${module}/:id/:label/:page/`}
                                       exact={true}
                                       component="app-details"/>
                      ])}
                    {ALL_MODULES
                    .map((module) => [
                      <stencil-route componentProps={{currentModule: module}}
                                     url={`${this.root}${PATH.FILTER}/${module}/:page`}
                                     component="app-filter"/>,
                      <stencil-route componentProps={{currentModule: module}}
                                     url={`${this.root}${PATH.FILTER}/${module}/:page/`}
                                     component="app-filter"/>,
                      <stencil-route componentProps={{currentModule: module, currentPath:PATH.FILTER}}
                                     url={`${this.root}${PATH.FILTER_VALUES}/${PATH.FILTER}/${module}`}
                                     component="app-values"/>,
                      <stencil-route componentProps={{currentModule: module, currentPath:PATH.FILTER}}
                                     url={`${this.root}${PATH.FILTER_VALUES}/${PATH.FILTER}/${module}/`}
                                     component="app-values"/>
                    ])}
                    {All_DESIGN_TYPES.map((type) => [
                      <stencil-route componentProps={{designType: type}}
                                     url={`${this.root}${PATH.FIND_BY_DESIGN}/${type}/:page`}
                                     exact={true}
                                     component="app-find-by-design"/>,
                      <stencil-route componentProps={{designType: type}}
                                     url={`${this.root}${PATH.FIND_BY_DESIGN}/${type}/:page/`}
                                     exact={true}
                                     component="app-find-by-design"/>,
                      <stencil-route componentProps={{currentModule: type, currentPath:PATH.FIND_BY_DESIGN}}
                                     url={`${this.root}${PATH.FILTER_VALUES}/${PATH.FIND_BY_DESIGN}/${type}`}
                                     exact={true}
                                     component="app-values"/>,
                      <stencil-route componentProps={{currentModule: type, currentPath:PATH.FIND_BY_DESIGN}}
                                     url={`${this.root}${PATH.FILTER_VALUES}/${PATH.FIND_BY_DESIGN}/${type}/`}
                                     exact={true}
                                     component="app-values"/>
                    ])}
                    <stencil-route routeRender={
                      () => {
                        this.history.replace(this.root);
                        (<RenderHomePage isMobile={this.isMobile}/>)
                      }
                    }/>
                  </stencil-route-switch>
                </stencil-router>
              </div>
            }
            </main>
        </apollo-provider>
        <div class="no-display" style={{ display: 'none', height: '0' }} innerHTML={svgSprite} />
      </div>
    );
  }

  public async componentWillLoad(): Promise<void> {
    store.state.root = this.root;
    this.unWatchMedia = watchMediaQuery((match) => {
      this.isMobile = match;
    })
  }

  public async disconnectedCallback(): Promise<void> {
    this.unWatchMedia();
  }
}

injectHistory(AppRoot);
